import type { SubmittedFormDetails } from '@epics/taskFormManagement/types';
import { VIEW_SUBMITTED_FORM_LABELS as L } from '@epics/taskFormManagement/constants/labels';

interface Props {
  data: SubmittedFormDetails;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return L.EMPTY_VALUE;
  }
  const part = dateStr.split(/[ T]/)[0] ?? '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(part);
  return m ? `${m[1]}/${m[2]}/${m[3]}` : dateStr;
}

function val(v: string | null | undefined): string {
  return v?.trim() ? v.trim() : L.EMPTY_VALUE;
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
      <p className="text-[18px] font-medium text-on-surface-variant">{title}</p>
      {children}
    </div>
  );
}

interface FieldGridProps {
  children: React.ReactNode;
}

function FieldGrid({ children }: FieldGridProps) {
  return (
    <div
      className="grid gap-x-16 gap-y-8"
      style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
    >
      {children}
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
}

function Field({ label, value }: FieldProps) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <p className="text-bodySmall font-normal text-on-surface-variant">
        {label}
      </p>
      <p className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10">
        {value}
      </p>
    </div>
  );
}

export default function BasicDetailsTab({ data }: Props) {
  const {
    service_request,
    technician,
    client,
    facility,
    equipment,
    task_form,
  } = data;

  return (
    <div className="flex flex-col gap-6 m-[6px]">
      {/* Basic Details */}
      <Section title={L.SECTION_BASIC_DETAILS}>
        <FieldGrid>
          <Field label={L.FIELD_FORM_NAME} value={val(task_form.form_name)} />
          <Field label={L.FIELD_FORM_ID} value={val(task_form.form_id)} />
          <Field
            label={L.FIELD_SR_ID}
            value={val(service_request.service_request_id)}
          />
          <Field
            label={L.FIELD_TECHNICIAN_ID}
            value={val(technician.technician_id)}
          />
          <Field label={L.FIELD_TECHNICIAN_NAME} value={val(technician.name)} />
          <Field
            label={L.FIELD_DATE_OF_SERVICE}
            value={formatDate(service_request.date_of_service)}
          />
        </FieldGrid>
      </Section>

      {/* Client Details */}
      <Section title={L.SECTION_CLIENT}>
        <FieldGrid>
          <Field label={L.FIELD_CLIENT_ID} value={val(client.client_id)} />
          <Field label={L.FIELD_CLIENT_NAME} value={val(client.name)} />
          <Field label={L.FIELD_CLIENT_EMAIL} value={val(client.email)} />
          <Field
            label={L.FIELD_CLIENT_PHONE}
            value={val(client.phone_number)}
          />
        </FieldGrid>
      </Section>

      {/* Facility Details */}
      <Section title={L.SECTION_FACILITY}>
        <FieldGrid>
          <Field
            label={L.FIELD_FACILITY_ID}
            value={val(facility.facility_id)}
          />
          <Field label={L.FIELD_FACILITY_NAME} value={val(facility.name)} />
        </FieldGrid>
      </Section>

      {/* Location Details */}
      <Section title={L.SECTION_LOCATION}>
        <FieldGrid>
          <Field
            label={L.FIELD_ADDRESS_LINE1}
            value={val(facility.address_line1)}
          />
          <Field
            label={L.FIELD_ADDRESS_LINE2}
            value={val(facility.address_line2)}
          />
          <Field label={L.FIELD_CITY} value={val(facility.city)} />
          <Field label={L.FIELD_STATE} value={val(facility.state)} />
          <Field label={L.FIELD_POSTAL} value={val(facility.postal_code)} />
          <Field label={L.FIELD_COUNTRY} value={val(facility.country)} />
        </FieldGrid>
      </Section>

      {/* Equipment Details */}
      <Section title={L.SECTION_EQUIPMENT}>
        <FieldGrid>
          <Field label={L.FIELD_EQUIPMENT_NAME} value={val(equipment.name)} />
          <Field
            label={L.FIELD_EQUIPMENT_ID}
            value={val(equipment.equipment_id)}
          />
          <Field
            label={L.FIELD_SERIAL_NUMBER}
            value={val(equipment.serial_number)}
          />
          <Field
            label={L.FIELD_MODEL_NUMBER}
            value={val(equipment.model_name)}
          />
          <Field
            label={L.FIELD_EQUIPMENT_DATE_OF_SERVICE}
            value={formatDate(equipment.date_of_service)}
          />
          <Field
            label={L.FIELD_EQUIPMENT_CATEGORY}
            value={val(equipment.category)}
          />
        </FieldGrid>
      </Section>
    </div>
  );
}
